/**
 * Pressure Sensor Calculations & Utilities (v2.4.0)
 *
 * Features:
 * - Atmospheric pressure monitoring (hPa/mbar)
 * - Weather forecasting based on pressure trends
 * - Altitude estimation from pressure readings
 * - Pressure statistics and analytics
 * - Multi-language support (Arabic/English)
 * - RTL support
 *
 * @version 2.4.0
 * @date 2026-01-23
 */

export type PressureLevel = 'very_low' | 'low' | 'normal' | 'high' | 'very_high';
export type WeatherForecast = 'stormy' | 'rainy' | 'cloudy' | 'fair' | 'sunny';
export type PressureTrend = 'rising' | 'stable' | 'falling';

export interface PressureReading {
  pressure: number; // hPa (hectopascals) or mbar
  timestamp: Date;
  temperature?: number; // For altitude correction
}

export interface PressureStats {
  avgPressure: number;
  minPressure: number;
  maxPressure: number;
  pressureRange: number;
  risingHours: number;
  stableHours: number;
  fallingHours: number;
  avgAltitude: number;
  weatherStability: 'very_stable' | 'stable' | 'changing' | 'unstable';
  forecastAccuracy: number; // percentage
}

export interface AltitudeInfo {
  estimatedAltitude: number; // meters above sea level
  confidenceLevel: 'high' | 'medium' | 'low';
  pressureAtSeaLevel: number; // normalized to sea level
}

export interface WeatherPrediction {
  forecast: WeatherForecast;
  confidence: number; // 0-100
  description: string;
  hoursAhead: number;
}

export interface PressureStatus {
  currentPressure: number;
  pressureLevel: PressureLevel;
  trend: PressureTrend;
  trendStrength: 'strong' | 'moderate' | 'weak';
  weatherForecast: WeatherForecast;
  altitudeEstimate: AltitudeInfo;
  todayStats: PressureStats;
  yesterdayStats: PressureStats;
  weekStats: PressureStats;
  monthStats: PressureStats;
  predictions: WeatherPrediction[];
  recommendations: string[];
}

// ==================== Core Functions ====================

/**
 * Classify pressure level
 * Standard atmospheric pressure at sea level: 1013.25 hPa
 */
export function getPressureLevel(pressure: number): PressureLevel {
  if (pressure < 980) return 'very_low';
  if (pressure < 1000) return 'low';
  if (pressure < 1020) return 'normal';
  if (pressure < 1040) return 'high';
  return 'very_high';
}

/**
 * Determine pressure trend from recent readings
 */
export function getPressureTrend(
  readings: PressureReading[],
  hours: number = 3,
): { trend: PressureTrend; rate: number; strength: 'strong' | 'moderate' | 'weak' } {
  if (readings.length < 2) {
    return { trend: 'stable', rate: 0, strength: 'weak' };
  }

  const now = new Date();
  const cutoff = new Date(now.getTime() - hours * 60 * 60 * 1000);
  const recentReadings = readings
    .filter((r) => new Date(r.timestamp) >= cutoff)
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  if (recentReadings.length < 2) {
    return { trend: 'stable', rate: 0, strength: 'weak' };
  }

  const firstPressure = recentReadings[0].pressure;
  const lastPressure = recentReadings[recentReadings.length - 1].pressure;
  const pressureChange = lastPressure - firstPressure;
  const ratePerHour = pressureChange / hours;

  let trend: PressureTrend;
  let strength: 'strong' | 'moderate' | 'weak';

  // Trend determination (3-hour standard)
  if (Math.abs(ratePerHour) < 0.5) {
    trend = 'stable';
    strength = 'weak';
  } else if (ratePerHour > 0) {
    trend = 'rising';
    strength = Math.abs(ratePerHour) > 2 ? 'strong' : 'moderate';
  } else {
    trend = 'falling';
    strength = Math.abs(ratePerHour) > 2 ? 'strong' : 'moderate';
  }

  return { trend, rate: ratePerHour, strength };
}

/**
 * Forecast weather based on pressure and trend
 */
export function getWeatherForecast(
  pressure: number,
  trend: PressureTrend,
  trendStrength: 'strong' | 'moderate' | 'weak',
): { forecast: WeatherForecast; confidence: number } {
  let forecast: WeatherForecast;
  let confidence: number;

  // Strong trends have higher confidence
  const strengthMultiplier =
    trendStrength === 'strong' ? 1.0 : trendStrength === 'moderate' ? 0.85 : 0.7;

  if (pressure < 980) {
    // Very low pressure
    forecast = trend === 'falling' ? 'stormy' : 'rainy';
    confidence = 90 * strengthMultiplier;
  } else if (pressure < 1000) {
    // Low pressure
    forecast = trend === 'falling' ? 'rainy' : 'cloudy';
    confidence = 80 * strengthMultiplier;
  } else if (pressure < 1020) {
    // Normal pressure
    if (trend === 'rising') {
      forecast = 'fair';
      confidence = 75 * strengthMultiplier;
    } else if (trend === 'falling') {
      forecast = 'cloudy';
      confidence = 75 * strengthMultiplier;
    } else {
      forecast = 'fair';
      confidence = 70 * strengthMultiplier;
    }
  } else if (pressure < 1040) {
    // High pressure
    forecast = trend === 'rising' ? 'sunny' : 'fair';
    confidence = 85 * strengthMultiplier;
  } else {
    // Very high pressure
    forecast = 'sunny';
    confidence = 90 * strengthMultiplier;
  }

  return { forecast, confidence };
}

/**
 * Estimate altitude from pressure
 * Uses barometric formula: h = 44330 * (1 - (P/P0)^0.1903)
 * P0 = standard sea level pressure (1013.25 hPa)
 */
export function estimateAltitude(pressure: number, temperature?: number): AltitudeInfo {
  const P0 = 1013.25; // Standard sea level pressure
  const altitude = 44330 * (1 - Math.pow(pressure / P0, 0.1903));

  // Confidence based on pressure validity and temperature correction
  let confidenceLevel: 'high' | 'medium' | 'low';
  if (pressure > 850 && pressure < 1100) {
    confidenceLevel = temperature !== undefined ? 'high' : 'medium';
  } else {
    confidenceLevel = 'low';
  }

  // Temperature correction if available (simplified)
  const correctedAltitude =
    temperature !== undefined
      ? altitude + (temperature - 15) * 0.5 // ~0.5m per degree difference from standard
      : altitude;

  return {
    estimatedAltitude: Math.round(correctedAltitude),
    confidenceLevel,
    pressureAtSeaLevel: P0,
  };
}

/**
 * Calculate average pressure over period
 */
export function calculateAveragePressure(
  readings: PressureReading[],
  startDate: Date,
  endDate: Date,
): number {
  const filtered = readings.filter((r) => {
    const t = new Date(r.timestamp);
    return t >= startDate && t <= endDate;
  });

  if (filtered.length === 0) return 0;

  const sum = filtered.reduce((acc, r) => acc + r.pressure, 0);
  return sum / filtered.length;
}

/**
 * Calculate comprehensive pressure statistics
 */
export function getPressureStatistics(
  readings: PressureReading[],
  startDate: Date,
  endDate: Date,
): PressureStats {
  const filtered = readings.filter((r) => {
    const t = new Date(r.timestamp);
    return t >= startDate && t <= endDate;
  });

  if (filtered.length === 0) {
    return {
      avgPressure: 0,
      minPressure: 0,
      maxPressure: 0,
      pressureRange: 0,
      risingHours: 0,
      stableHours: 0,
      fallingHours: 0,
      avgAltitude: 0,
      weatherStability: 'stable',
      forecastAccuracy: 0,
    };
  }

  // Basic stats
  const pressures = filtered.map((r) => r.pressure);
  const avgPressure = pressures.reduce((a, b) => a + b, 0) / pressures.length;
  const minPressure = Math.min(...pressures);
  const maxPressure = Math.max(...pressures);
  const pressureRange = maxPressure - minPressure;

  // Trend analysis
  let risingHours = 0;
  let stableHours = 0;
  let fallingHours = 0;

  for (let i = 1; i < filtered.length; i++) {
    const change = filtered[i].pressure - filtered[i - 1].pressure;
    const hoursDiff =
      (new Date(filtered[i].timestamp).getTime() - new Date(filtered[i - 1].timestamp).getTime()) /
      (1000 * 60 * 60);

    if (Math.abs(change) < 0.5) {
      stableHours += hoursDiff;
    } else if (change > 0) {
      risingHours += hoursDiff;
    } else {
      fallingHours += hoursDiff;
    }
  }

  // Altitude estimation
  const avgAltitude = estimateAltitude(avgPressure).estimatedAltitude;

  // Weather stability (based on pressure range)
  let weatherStability: 'very_stable' | 'stable' | 'changing' | 'unstable';
  if (pressureRange < 5) weatherStability = 'very_stable';
  else if (pressureRange < 10) weatherStability = 'stable';
  else if (pressureRange < 20) weatherStability = 'changing';
  else weatherStability = 'unstable';

  return {
    avgPressure,
    minPressure,
    maxPressure,
    pressureRange,
    risingHours,
    stableHours,
    fallingHours,
    avgAltitude,
    weatherStability,
    forecastAccuracy: 75, // Mock - would need historical comparison
  };
}

/**
 * Generate weather predictions for next hours
 */
export function generateWeatherPredictions(
  currentPressure: number,
  trend: PressureTrend,
  trendStrength: 'strong' | 'moderate' | 'weak',
  trendRate: number,
  language: 'ar' | 'en',
): WeatherPrediction[] {
  const predictions: WeatherPrediction[] = [];

  // 3-hour prediction
  const pressure3h = currentPressure + trendRate * 3;
  const forecast3h = getWeatherForecast(pressure3h, trend, trendStrength);
  predictions.push({
    forecast: forecast3h.forecast,
    confidence: forecast3h.confidence,
    description: formatWeatherDescription(forecast3h.forecast, language),
    hoursAhead: 3,
  });

  // 6-hour prediction (with reduced confidence)
  const pressure6h = currentPressure + trendRate * 6;
  const forecast6h = getWeatherForecast(pressure6h, trend, trendStrength);
  predictions.push({
    forecast: forecast6h.forecast,
    confidence: forecast6h.confidence * 0.85,
    description: formatWeatherDescription(forecast6h.forecast, language),
    hoursAhead: 6,
  });

  // 12-hour prediction (with further reduced confidence)
  const pressure12h = currentPressure + trendRate * 12 * 0.7; // Trend slows over time
  const forecast12h = getWeatherForecast(pressure12h, trend, trendStrength);
  predictions.push({
    forecast: forecast12h.forecast,
    confidence: forecast12h.confidence * 0.7,
    description: formatWeatherDescription(forecast12h.forecast, language),
    hoursAhead: 12,
  });

  return predictions;
}

/**
 * Generate context-aware recommendations
 */
export function generatePressureRecommendations(
  pressure: number,
  trend: PressureTrend,
  weatherForecast: WeatherForecast,
  stats: PressureStats,
  language: 'ar' | 'en',
): string[] {
  const recommendations: string[] = [];

  // Weather-based recommendations
  if (weatherForecast === 'stormy' || weatherForecast === 'rainy') {
    recommendations.push(
      language === 'ar' ? 'توقع أمطار - جهز المعدات اللازمة' : 'Expect rain - prepare equipment',
    );
  }

  if (trend === 'falling' && pressure < 1000) {
    recommendations.push(
      language === 'ar'
        ? 'الضغط الجوي ينخفض - قد يتدهور الطقس'
        : 'Pressure dropping - weather may worsen',
    );
  }

  if (trend === 'rising' && pressure > 1020) {
    recommendations.push(
      language === 'ar'
        ? 'الضغط الجوي يرتفع - طقس مستقر متوقع'
        : 'Pressure rising - stable weather expected',
    );
  }

  // Altitude-related
  if (stats.avgAltitude > 2000) {
    recommendations.push(
      language === 'ar'
        ? 'ارتفاع عالي مكتشف - راقب أعراض نقص الأكسجين'
        : 'High altitude detected - monitor oxygen levels',
    );
  }

  // Stability recommendations
  if (stats.weatherStability === 'unstable') {
    recommendations.push(
      language === 'ar'
        ? 'طقس غير مستقر - راقب التحديثات باستمرار'
        : 'Unstable weather - monitor updates frequently',
    );
  } else if (stats.weatherStability === 'very_stable') {
    recommendations.push(
      language === 'ar'
        ? 'طقس مستقر جداً - ظروف مثالية للأنشطة الخارجية'
        : 'Very stable weather - ideal for outdoor activities',
    );
  }

  // Health recommendations
  if (pressure < 990 || pressure > 1030) {
    recommendations.push(
      language === 'ar'
        ? 'ضغط جوي شاذ - قد يؤثر على الأشخاص الحساسين'
        : 'Abnormal pressure - may affect sensitive individuals',
    );
  }

  return recommendations.slice(0, 3); // Max 3 recommendations
}

/**
 * Get comprehensive pressure status
 */
export function getPressureStatus(
  readings: PressureReading[],
  currentPressure: number,
): PressureStatus {
  const now = new Date();

  // Calculate time periods
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday.getTime() - 24 * 60 * 60 * 1000);
  const startOfWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  // Get statistics
  const todayStats = getPressureStatistics(readings, startOfToday, now);
  const yesterdayStats = getPressureStatistics(readings, startOfYesterday, startOfToday);
  const weekStats = getPressureStatistics(readings, startOfWeek, now);
  const monthStats = getPressureStatistics(readings, startOfMonth, now);

  // Get trend
  const trendInfo = getPressureTrend(readings, 3);

  // Get weather forecast
  const weatherInfo = getWeatherForecast(currentPressure, trendInfo.trend, trendInfo.strength);

  // Get altitude
  const altitudeEstimate = estimateAltitude(currentPressure);

  // Get level
  const pressureLevel = getPressureLevel(currentPressure);

  // Generate predictions
  const predictions = generateWeatherPredictions(
    currentPressure,
    trendInfo.trend,
    trendInfo.strength,
    trendInfo.rate,
    'en',
  );

  // Generate recommendations
  const recommendations = generatePressureRecommendations(
    currentPressure,
    trendInfo.trend,
    weatherInfo.forecast,
    todayStats,
    'en',
  );

  return {
    currentPressure,
    pressureLevel,
    trend: trendInfo.trend,
    trendStrength: trendInfo.strength,
    weatherForecast: weatherInfo.forecast,
    altitudeEstimate,
    todayStats,
    yesterdayStats,
    weekStats,
    monthStats,
    predictions,
    recommendations,
  };
}

// ==================== Formatting Functions ====================

/**
 * Format pressure with unit
 */
export function formatPressure(pressure: number, language: 'ar' | 'en'): string {
  if (language === 'ar') {
    const arabicNum = pressure.toFixed(1).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    return `${arabicNum} هكتوباسكال`;
  }
  return `${pressure.toFixed(1)} hPa`;
}

/**
 * Format pressure level
 */
export function formatPressureLevel(level: PressureLevel, language: 'ar' | 'en'): string {
  const labels: Record<PressureLevel, { ar: string; en: string }> = {
    very_low: { ar: 'منخفض جداً', en: 'Very Low' },
    low: { ar: 'منخفض', en: 'Low' },
    normal: { ar: 'طبيعي', en: 'Normal' },
    high: { ar: 'مرتفع', en: 'High' },
    very_high: { ar: 'مرتفع جداً', en: 'Very High' },
  };
  return labels[level][language];
}

/**
 * Format pressure trend
 */
export function formatPressureTrend(trend: PressureTrend, language: 'ar' | 'en'): string {
  const labels: Record<PressureTrend, { ar: string; en: string }> = {
    rising: { ar: 'يرتفع', en: 'Rising' },
    stable: { ar: 'مستقر', en: 'Stable' },
    falling: { ar: 'ينخفض', en: 'Falling' },
  };
  return labels[trend][language];
}

/**
 * Format weather forecast
 */
export function formatWeatherForecast(forecast: WeatherForecast, language: 'ar' | 'en'): string {
  const labels: Record<WeatherForecast, { ar: string; en: string }> = {
    stormy: { ar: 'عاصف', en: 'Stormy' },
    rainy: { ar: 'ممطر', en: 'Rainy' },
    cloudy: { ar: 'غائم', en: 'Cloudy' },
    fair: { ar: 'صحو', en: 'Fair' },
    sunny: { ar: 'مشمس', en: 'Sunny' },
  };
  return labels[forecast][language];
}

/**
 * Format weather description
 */
export function formatWeatherDescription(forecast: WeatherForecast, language: 'ar' | 'en'): string {
  const descriptions: Record<WeatherForecast, { ar: string; en: string }> = {
    stormy: { ar: 'طقس عاصف مع أمطار غزيرة متوقعة', en: 'Stormy weather with heavy rain expected' },
    rainy: { ar: 'أمطار محتملة خلال الساعات القادمة', en: 'Rainfall likely in coming hours' },
    cloudy: {
      ar: 'غيوم متزايدة مع احتمال أمطار خفيفة',
      en: 'Increasing clouds with possible light rain',
    },
    fair: { ar: 'طقس معتدل مع غيوم متفرقة', en: 'Fair weather with scattered clouds' },
    sunny: { ar: 'سماء صافية وطقس مشمس', en: 'Clear skies and sunny weather' },
  };
  return descriptions[forecast][language];
}

/**
 * Format altitude
 */
export function formatAltitude(altitude: number, language: 'ar' | 'en'): string {
  if (language === 'ar') {
    const arabicNum = altitude.toString().replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    return `${arabicNum} م`;
  }
  return `${altitude} m`;
}

/**
 * Format trend strength
 */
export function formatTrendStrength(
  strength: 'strong' | 'moderate' | 'weak',
  language: 'ar' | 'en',
): string {
  const labels: Record<string, { ar: string; en: string }> = {
    strong: { ar: 'قوي', en: 'Strong' },
    moderate: { ar: 'متوسط', en: 'Moderate' },
    weak: { ar: 'ضعيف', en: 'Weak' },
  };
  return labels[strength][language];
}

/**
 * Format weather stability
 */
export function formatWeatherStability(stability: string, language: 'ar' | 'en'): string {
  const labels: Record<string, { ar: string; en: string }> = {
    very_stable: { ar: 'مستقر جداً', en: 'Very Stable' },
    stable: { ar: 'مستقر', en: 'Stable' },
    changing: { ar: 'متغير', en: 'Changing' },
    unstable: { ar: 'غير مستقر', en: 'Unstable' },
  };
  return labels[stability]?.[language] || stability;
}

// ==================== Color Functions ====================

/**
 * Get color for pressure level
 */
export function getPressureLevelColor(level: PressureLevel): string {
  const colors: Record<PressureLevel, string> = {
    very_low: '#ef4444', // Red
    low: '#f59e0b', // Orange
    normal: '#10b981', // Green
    high: '#3b82f6', // Blue
    very_high: '#8b5cf6', // Purple
  };
  return colors[level];
}

/**
 * Get color for weather forecast
 */
export function getWeatherForecastColor(forecast: WeatherForecast): string {
  const colors: Record<WeatherForecast, string> = {
    stormy: '#7c2d12', // Dark red-brown
    rainy: '#1e40af', // Dark blue
    cloudy: '#6b7280', // Gray
    fair: '#059669', // Green
    sunny: '#f59e0b', // Yellow-orange
  };
  return colors[forecast];
}

/**
 * Get color for pressure trend
 */
export function getPressureTrendColor(trend: PressureTrend): string {
  const colors: Record<PressureTrend, string> = {
    rising: '#10b981', // Green
    stable: '#6b7280', // Gray
    falling: '#ef4444', // Red
  };
  return colors[trend];
}

/**
 * Get color for pressure value (gradient based on absolute value)
 */
export function getPressureColor(pressure: number): string {
  if (pressure < 980) return '#ef4444'; // Very low - red
  if (pressure < 1000) return '#f59e0b'; // Low - orange
  if (pressure < 1020) return '#10b981'; // Normal - green
  if (pressure < 1040) return '#3b82f6'; // High - blue
  return '#8b5cf6'; // Very high - purple
}
